import type {
  SiteCmsBlock,
  SiteCmsCompetitionCard,
  SiteCmsDocumentResponse,
  SiteCmsDraftResponse,
  SiteCmsImageResponse,
} from '@shared/types/entities';

import SiteCmsHtmlEditor from './SiteCmsHtmlEditor';
import SiteCmsImagePicker from './SiteCmsImagePicker';
import SiteCmsLinkPicker from './SiteCmsLinkPicker';
import SiteCmsRichTextEditor from './SiteCmsRichTextEditor';

type Props = {
  draft: SiteCmsDraftResponse;
  block: SiteCmsBlock;
  onChange: (block: SiteCmsBlock) => void;
  images: SiteCmsImageResponse[];
  documents: SiteCmsDocumentResponse[];
  competitions: SiteCmsCompetitionCard[];
};
export default function SiteCmsSectionEditor({ block: b, onChange, draft, images, documents, competitions }: Props) {
  const input = (label: string, value: string, change: (text: string) => void, max = 180) => (
    <label>
      {label}
      <input value={value} maxLength={max} onChange={e => change(e.target.value)} />
    </label>
  );
  const image = (value: string | null, change: (id: string | null) => void, inherited = false) => (
    <SiteCmsImagePicker
      ownerType={draft.ownerType}
      ownerId={draft.ownerId}
      value={value}
      onChange={change}
      fallback={inherited ? 'Bannière héritée de l’apparence du site' : undefined}
    />
  );
  const link = (value: string, change: (href: string) => void, optional = false) => (
    <SiteCmsLinkPicker
      value={value}
      onChange={change}
      pages={draft.pages}
      documents={documents}
      competitions={competitions}
      optional={optional}
    />
  );
  const resources = { pages: draft.pages, documents, competitions };
  const reorder = <T,>(values: T[], index: number, step: number): T[] => {
    const next = [...values];
    const at = index + step;
    if (at >= 0 && at < next.length) [next[index], next[at]] = [next[at], next[index]];
    return next;
  };

  switch (b.type) {
    case 'heading':
      return (
        <div className='cms-row'>
          <select
            aria-label='Niveau du titre'
            value={b.level}
            onChange={e => onChange({ ...b, level: Number(e.target.value) as 2 | 3 })}
          >
            <option value={2}>Titre de section</option>
            <option value={3}>Sous-titre</option>
          </select>
          {input('Texte du titre', b.text, text => onChange({ ...b, text }))}
        </div>
      );
    case 'paragraph':
      return (
        <label>
          Paragraphe
          <textarea rows={5} maxLength={5000} value={b.text} onChange={e => onChange({ ...b, text: e.target.value })} />
        </label>
      );
    case 'button':
      return (
        <div className='cms-form-grid'>
          {input('Libellé du bouton', b.label, label => onChange({ ...b, label }), 90)}
          {link(b.href, href => onChange({ ...b, href }))}
        </div>
      );
    case 'text':
      return (
        <div className='cms-stack'>
          {input('Titre de la section', b.title, title => onChange({ ...b, title }))}
          <SiteCmsRichTextEditor
            resources={resources}
            text={b.text}
            rich={b.rich}
            onChange={(text, rich) => onChange({ ...b, text, rich })}
          />
        </div>
      );
    case 'imageText':
      return (
        <div className='cms-stack'>
          {input('Titre', b.title, title => onChange({ ...b, title }))}
          <div className='cms-form-grid'>
            {image(b.imageId, imageId => onChange({ ...b, imageId }))}
            <label>
              Position de l’image
              <select value={b.side} onChange={e => onChange({ ...b, side: e.target.value as 'left' | 'right' })}>
                <option value='left'>À gauche</option>
                <option value='right'>À droite</option>
              </select>
            </label>
            {input('Description alternative pour cette section', b.alt, alt => onChange({ ...b, alt }), 250)}
            <p className='cms-hint'>
              {b.alt
                ? 'Texte personnalisé ici. Effacez-le pour reprendre celui de la bibliothèque.'
                : `Hérité de la bibliothèque : ${images.find(i => i.id === b.imageId)?.alt || 'aucune description disponible'}`}
            </p>
          </div>
          <SiteCmsRichTextEditor
            resources={resources}
            text={b.text}
            rich={b.rich}
            onChange={(text, rich) => onChange({ ...b, text, rich })}
          />
        </div>
      );
    case 'cover':
      return (
        <div className='cms-stack'>
          {input('Titre de l’en-tête', b.title, title => onChange({ ...b, title }))}
          <label>
            Accroche
            <textarea
              rows={3}
              value={b.text}
              maxLength={5000}
              onChange={e => onChange({ ...b, text: e.target.value })}
            />
          </label>
          <div className='cms-hero-media-grid'>
            <section className='cms-hero-media-option'>
              <h4>Image de bannière latérale</h4>
              <p className='cms-hint'>
                Image facultative à côté du titre et du bouton. Si elle est vide, l’image choisie dans Apparence sera
                utilisée.
              </p>
              {image(b.imageId, imageId => onChange({ ...b, imageId }), true)}
              <small>
                {b.imageId
                  ? 'Cette page utilise sa propre bannière.'
                  : 'Bannière héritée de l’apparence du site, si définie.'}
              </small>
            </section>
            <section className='cms-hero-media-option'>
              <h4>Fond du bloc hero (facultatif)</h4>
              <p className='cms-hint'>
                Image derrière le titre, le bouton et la bannière. Un voile de contraste protège la lisibilité.
                Indépendant du fond global du site.
              </p>
              <SiteCmsImagePicker
                ownerType={draft.ownerType}
                ownerId={draft.ownerId}
                label='Image de fond du hero'
                value={b.backgroundImageId ?? null}
                onChange={backgroundImageId => onChange({ ...b, backgroundImageId })}
              />
              <div className='cms-field-label'>
                Cadrage du fond du hero
                <div className='cms-segmented' role='group' aria-label='Cadrage du fond du hero'>
                  {(
                    [
                      ['top', 'Haut'],
                      ['center', 'Centre'],
                      ['bottom', 'Bas'],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type='button'
                      disabled={!b.backgroundImageId}
                      aria-pressed={(b.backgroundPosition ?? 'center') === value}
                      onClick={() => onChange({ ...b, backgroundPosition: value })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </section>
          </div>
          <div className='cms-form-grid'>
            {input('Libellé du bouton (facultatif)', b.label, label => onChange({ ...b, label }), 90)}
            {link(b.href, href => onChange({ ...b, href }), true)}
          </div>
        </div>
      );
    case 'cta':
      return (
        <div className='cms-stack'>
          {input('Titre', b.title, title => onChange({ ...b, title }))}
          <label>
            Message
            <textarea
              rows={3}
              maxLength={5000}
              value={b.text}
              onChange={e => onChange({ ...b, text: e.target.value })}
            />
          </label>
          <div className='cms-form-grid'>
            {input('Libellé du bouton', b.label, label => onChange({ ...b, label }), 90)}
            {link(b.href, href => onChange({ ...b, href }))}
          </div>
        </div>
      );
    case 'info':
      return (
        <div className='cms-stack'>
          {input('Titre', b.title, title => onChange({ ...b, title }))}
          {b.items.map((item, index) => (
            <div className='cms-repeat-row' key={index}>
              {input(
                'Libellé',
                item.label,
                label => onChange({ ...b, items: b.items.map((x, i) => (i === index ? { ...x, label } : x)) }),
                100,
              )}
              {input(
                'Information',
                item.text,
                text => onChange({ ...b, items: b.items.map((x, i) => (i === index ? { ...x, text } : x)) }),
                1000,
              )}
              <button
                type='button'
                className='cms-link-button cms-danger'
                onClick={() => onChange({ ...b, items: b.items.filter((_, i) => i !== index) })}
              >
                Retirer
              </button>
            </div>
          ))}
          <button
            type='button'
            className='cms-button'
            disabled={b.items.length >= 12}
            onClick={() =>
              onChange({ ...b, items: [...b.items, { label: 'Horaires', text: 'Précisez vos informations' }] })
            }
          >
            Ajouter une information
          </button>
        </div>
      );
    case 'documents':
      return (
        <div className='cms-stack'>
          {input('Titre', b.title, title => onChange({ ...b, title }))}
          <div className='cms-select-list'>
            {documents.map(doc => (
              <label key={doc.id}>
                <input
                  type='checkbox'
                  checked={b.slugs.includes(doc.slug)}
                  onChange={e =>
                    onChange({
                      ...b,
                      slugs: e.target.checked ? [...b.slugs, doc.slug] : b.slugs.filter(x => x !== doc.slug),
                    })
                  }
                />
                <span>
                  <b>{doc.title}</b>
                  <small>
                    {doc.fileName} · {Math.ceil(doc.sizeBytes / 1024)} Ko
                  </small>
                </span>
              </label>
            ))}
          </div>
          <div className='cms-selection-order'>
            {b.slugs.map((slug, index) => (
              <div key={slug}>
                <span>{documents.find(d => d.slug === slug)?.title ?? `Document indisponible : ${slug}`}</span>
                <button
                  type='button'
                  className='cms-link-button'
                  disabled={index === 0}
                  onClick={() => onChange({ ...b, slugs: reorder(b.slugs, index, -1) })}
                >
                  Monter
                </button>
                <button
                  type='button'
                  className='cms-link-button'
                  onClick={() => onChange({ ...b, slugs: b.slugs.filter(s => s !== slug) })}
                >
                  Retirer
                </button>
              </div>
            ))}
          </div>
          {!documents.length && (
            <p className='cms-hint'>Ajoutez un PDF dans Images & documents, puis sélectionnez-le ici.</p>
          )}
          <p className='cms-hint'>Ces fichiers deviendront publics lors de la publication de cette page.</p>
        </div>
      );
    case 'competitions':
      return (
        <div className='cms-stack'>
          {input('Titre', b.title, title => onChange({ ...b, title }))}
          <div className='cms-select-list'>
            {competitions.map(card => (
              <label key={card.id}>
                <input
                  type='checkbox'
                  checked={b.ids.includes(card.id)}
                  disabled={!b.ids.includes(card.id) && b.ids.length >= 12}
                  onChange={e =>
                    onChange({ ...b, ids: e.target.checked ? [...b.ids, card.id] : b.ids.filter(id => id !== card.id) })
                  }
                />
                <span>
                  <b>{card.title}</b>
                  <small>
                    {card.location} · {card.date ? new Date(card.date).toLocaleDateString('fr-FR') : 'Date à préciser'}
                  </small>
                </span>
              </label>
            ))}
          </div>
          <div className='cms-selection-order'>
            {b.ids.map((id, index) => (
              <div key={id}>
                <span>{competitions.find(c => c.id === id)?.title ?? `Concours indisponible : #${id}`}</span>
                <button
                  type='button'
                  className='cms-link-button'
                  disabled={index === 0}
                  onClick={() => onChange({ ...b, ids: reorder(b.ids, index, -1) })}
                >
                  Monter
                </button>
                <button
                  type='button'
                  className='cms-link-button'
                  onClick={() => onChange({ ...b, ids: b.ids.filter(x => x !== id) })}
                >
                  Retirer
                </button>
              </div>
            ))}
          </div>
          <p className='cms-hint'>
            Les cartes sont actualisées lors de la publication. L’inscription et l’état courant restent sur
            BoulesConnect. {b.ids.length}/12 concours.
          </p>
          {!competitions.length && (
            <p className='cms-hint'>
              Aucun concours public disponible : un brouillon de concours ne peut pas être affiché ici.
            </p>
          )}
        </div>
      );
    case 'html':
      return (
        <div className='cms-stack'>
          {input('Nom interne de la section (facultatif, non publié)', b.title, title => onChange({ ...b, title }))}
          <SiteCmsHtmlEditor
            ownerType={draft.ownerType}
            ownerId={draft.ownerId}
            doc={b.doc}
            resources={resources}
            onChange={doc => onChange({ ...b, doc })}
          />
        </div>
      );
    case 'partners':
      return (
        <div className='cms-stack'>
          {input('Titre', b.title, title => onChange({ ...b, title }))}
          {b.items.map((item, index) => (
            <div className='cms-partner-edit' key={index}>
              <div className='cms-form-grid'>
                {input(
                  'Nom du partenaire',
                  item.name,
                  name => onChange({ ...b, items: b.items.map((x, i) => (i === index ? { ...x, name } : x)) }),
                  120,
                )}
                {link(
                  item.url,
                  url => onChange({ ...b, items: b.items.map((x, i) => (i === index ? { ...x, url } : x)) }),
                  true,
                )}
                {image(item.imageId, imageId =>
                  onChange({ ...b, items: b.items.map((x, i) => (i === index ? { ...x, imageId } : x)) }),
                )}
              </div>
              <button
                type='button'
                className='cms-link-button cms-danger'
                onClick={() => onChange({ ...b, items: b.items.filter((_, i) => i !== index) })}
              >
                Retirer ce partenaire
              </button>
            </div>
          ))}
          <button
            type='button'
            className='cms-button'
            disabled={b.items.length >= 24}
            onClick={() => onChange({ ...b, items: [...b.items, { name: 'Partenaire', url: '', imageId: null }] })}
          >
            Ajouter un partenaire
          </button>
        </div>
      );
  }
}
